import { Inject } from '@odinjs/odin';

import { BeneficiarioInclusaoEntity, catchRenderException, Endpoint, FULL_ROUTES, IeAnexo, onCatchError, setPredefinedFiles, StatusBeneficiario, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { BaseFileUploaderAPI, BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues, ChecklistAPI, ContextualBarAPI, DateFieldAPI, FetchAPI, FieldAPI, FormAPI, MapperAPI, PanelAPI, StampAPI, TextAPI, ToastNotificationAPI, UnboxedAutocompleteAPI, WizardAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnAfterNavigate, OnBeforeFetch, OnBlur, OnChange, OnMount, OnSelect, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { AbortableFetchEvent, AbortableNavigateEvent, ChangeEvent, FetchEvent, MountEvent, SelectEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod, RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject, isEmptyObject, isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { InclusaoBeneficiarioNovaInclusaoAliases as Aliases, FragmentNames } from '../../codegen';
import { Anexo } from '../../interfaces/anexo';
import { EnderecoData } from '../../interfaces/logradouro';
import BaseController from '../BaseController';
import { BeneficiarioLoteData } from '../lote/lote-utils';

interface DataAdesao {
  dtAdesao: string;
  ieAlteraData: string;
}

interface Atributo {
  nmAtributo: string;
  ieVisualizarCampo: string;
  ieObrigatorio: string;
}

interface ErrorMessage {
  expressao: string;
  anexoPendente: string;
}

interface RecordAnexos {
  anexos: Array<Anexo>;
}

const forms = [Aliases.FORM_DADOS_BENEFICIARIO, Aliases.FORM_PORTABILIDADE, Aliases.FORM_SCA, Aliases.FORM_COMPARTILHAMENTO_RISCO, Aliases.FORM_ANEXOS];

@Controller({ fragment: FragmentNames.INCLUSAO_BENEFICIARIO_NOVA_INCLUSAO })
export default class InclusaoBeneficiarioNovaInclusaoController extends BaseController {

  private identifierDadosContratacao = Aliases.WIZARD_STEP_DADOS_CONTRATACAO;
  private identifierPortabilidade = Aliases.WIZARD_STEP_PORTABILIDADE;
  private identifierSca = Aliases.WIZARD_STEP_SCA;
  private identifierCompartilhamentoRisco = Aliases.WIZARD_STEP_COMPARTILHAMENTO_RISCO;
  private identifierAnexos = Aliases.WIZARD_STEP_ANEXOS;

  private formsApi: FormAPI[];
  private customFieldsFormDadosContratacao: FieldAPI[];
  private customFieldsFormDadosBeneficiario: FieldAPI[];
  private isDtContratacaoReadonly: boolean;
  private isNrSeqSubestipulanteReadonly: boolean;
  private isReenvio: boolean;
  private regraAnexos: Anexo[];

  @Inject(Aliases.CONTEXTUAL_BAR)
  contextualBar: ContextualBarAPI;
  @Inject(Aliases.WIZARD_NOVA_INCLUSAO)
  private wizardNovaInclusao: WizardAPI;

  @Inject(Aliases.FORM_DADOS_BENEFICIARIO)
  private formDadosBeneficiario: FormAPI;
  @Inject(Aliases.FORM_DADOS_CONTRATACAO)
  private formDadosContratacao: FormAPI;
  @Inject(Aliases.FORM_PORTABILIDADE)
  private formPortabilidade: FormAPI;
  @Inject(Aliases.FORM_SCA)
  private formSca: FormAPI;
  @Inject(Aliases.FORM_COMPARTILHAMENTO_RISCO)
  private formCompartilhamentoRisco: FormAPI;
  @Inject(Aliases.FORM_ANEXOS)
  private formAnexos: FormAPI;

  @Inject(Aliases.FETCH_DS_MUNICIPIO)
  private fetchDsMunicipio: FetchAPI;
  @Inject(Aliases.FIELD_DS_MUNICIPIO)
  private fieldDsMunicipio: FieldAPI;
  @Inject(Aliases.FIELD_ANEXOS)
  private fieldAnexos: FieldAPI;
  @Inject(Aliases.FIELD_CHECKBOX_ANEXOS)
  private fieldCheckboxAnexos: FieldAPI;
  @Inject(Aliases.FIELD_CHECK_ENDERECO_TITULAR)
  private fieldCheckEnderecoTitular: FieldAPI;

  @Inject(Aliases.AUTOCOMPLETE_NR_SEQ_SUBESTIPULANTE)
  private autocompleteNrSeqSubestipulante: UnboxedAutocompleteAPI;
  @Inject(Aliases.DATE_FIELD_DT_CONTRATACAO)
  private dateFieldDtContratacao: DateFieldAPI;

  @Inject(Aliases.CHECKLIST_SCA)
  private checklistSca: ChecklistAPI;

  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastNotification: ToastNotificationAPI;
  @Inject(Aliases.PANEL_LISTA_ANEXOS)
  private panelListaAnexos: PanelAPI;
  @Inject(Aliases.PANEL_CONTROL_CARD)
  private panelControlCardApi: PanelAPI;
  @Inject(Aliases.TEXT_MOTIVO)
  private textMotivoApi: TextAPI;
  @Inject(Aliases.STAMP_SITUACAO_LOTE)
  private stampSituacaoApi: StampAPI;
  @Inject(Aliases.FETCH_SCA)
  private fetchSCA: FetchAPI;

  @OnSubmit(Aliases.WIZARD_NOVA_INCLUSAO)
  onSubmit(): void {
    const lastStepIdentifier = this.wizardNovaInclusao.getCurrentStepIdentifier();

    const recordPortabilidade = catchRenderException(() => this.formPortabilidade.getRecord(), {});
    const recordCompartilhamentoRisco = catchRenderException(() => this.formCompartilhamentoRisco.getRecord(), {});
    const recordSca = catchRenderException(() => this.formSca.getRecord(), {});
    const recordAnexos: RecordAnexos = catchRenderException(() => this.formAnexos.getRecord(), {});
    const recordDadosBeneficiario = this.formDadosBeneficiario.getRecord() as unknown as BeneficiarioInclusaoEntity;
    const recordDadosContratacao = this.formDadosContratacao.getRecord() as unknown as BeneficiarioInclusaoEntity;

    recordAnexos.anexos = this.handleAnexos(recordAnexos.anexos);

    recordDadosContratacao.nrSeqMotivoInclusao = recordDadosContratacao.motivoInclusao.nrSeqMotivoInclusao;
    recordDadosBeneficiario.municipioNasc && (recordDadosBeneficiario.cdMunicipioIbgeNasc = recordDadosBeneficiario.municipioNasc.cdMunicipioIbge);
    recordDadosBeneficiario.cbo && (recordDadosBeneficiario.cdCbo = recordDadosBeneficiario.cbo.cdCbo);
    recordDadosBeneficiario.operadoraAnt && (recordDadosBeneficiario.nrSeqOperadoraAnt = recordDadosBeneficiario.operadoraAnt.nrSeqOperadoraAnt);
    recordCompartilhamentoRisco.congenere && (recordCompartilhamentoRisco.nrSeqCongenere = recordCompartilhamentoRisco.congenere.nrSeqCongenere);

    if (recordDadosBeneficiario.faixaSalarial) {
      recordDadosBeneficiario.nrSeqFaixaSalarial = recordDadosBeneficiario.faixaSalarial.nrSeqFaixaSalarial;
      recordDadosBeneficiario.nrNivelFaixaSalarial = recordDadosBeneficiario.faixaSalarial.nrNivelFaixaSalarial;
    } else {
      delete recordDadosBeneficiario.nrSeqFaixaSalarial;
      delete recordDadosBeneficiario.nrNivelFaixaSalarial;
    }

    const body = {
      entity: {
        ...recordDadosBeneficiario,
        ...recordDadosContratacao,
        ...recordPortabilidade,
        ...recordCompartilhamentoRisco,
      },
      ...recordSca,
      ...recordAnexos,
    };

    catchRenderException(() => this.toastNotification.setVisible(false));
    this.setStepDone(lastStepIdentifier, false);

    void this.getFetch(body)
      .then(() => this.historyService.push(FULL_ROUTES.BENEF_NOVA_INCLUSAO_CONSULTA))
      .catch(ex => {
        this.setStepDone(lastStepIdentifier, true);
        if (ex && ex.body) {
          if (isEmptyObject(ex.body)) {
            onCatchError(ex.body);
          }
          else {
            const mensagemErro = ex.body as ErrorMessage;
            if (isEmptyOrBlankString(mensagemErro.anexoPendente)) {
              onCatchError(mensagemErro.expressao);
            } else {
              const exception = catchRenderException(() => {
                this.toastNotification.setVisible(true);
                this.toastNotification.setMessage(`${MessageService.translate(mensagemErro.expressao)} (${mensagemErro.anexoPendente})`);

                if (!this.fieldAnexos.isVisible()) {
                  this.fieldCheckboxAnexos.setVisible(false);
                }

                this.fieldAnexos.setVisible(true);
                this.panelListaAnexos.setVisible(false);
              }, () => ToastServiceStipulator.warning(MessageService.translate(ex.body)));
              exception && exception();

            }
          }

        } else {
          onCatchError();
        }
      });
  }

  @OnMount(Aliases.PANEL_INCLUSAO_BENEFICIARIO_NOVA_INCLUSAO)
  onMountMain(): void {
    this.formsApi = [];
    this.customFieldsFormDadosContratacao = [];
    this.customFieldsFormDadosBeneficiario = [];
    this.isDtContratacaoReadonly = false;
    this.isNrSeqSubestipulanteReadonly = false;
    this.isReenvio = false;

    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const { path } = this.historyService.getMatch();

    this.isReenvio = path === FULL_ROUTES.BENEF_SEQ_REENVIAR_INCLUSAO;

    void HttpService.get(Endpoint.GET.INC_BENEF_ATRIBUTOS, { nrSeqContrato })
      .then(response => this.setCustomFields(response.body as Atributo[], this.customFieldsFormDadosContratacao));
  }

  @OnMount(Aliases.WIZARD_NOVA_INCLUSAO)
  onMountWizard(): void {
    const { nrSeqContrato, nrSeqInclusao } = this.historyService.getCurrentRouteParams();

    if (nrSeqInclusao) {

      this.isReenvio ? this.contextualBar.setTitle('stipulator.reenviar-inclusao') : this.contextualBar.setTitle('stipulator.detalhes');

      !this.isReenvio && this.validateStatus();

      void HttpService.get<Record<string, Value>>(Endpoint.GET.INC_BENEF_DADOS, { nrSeqInclusao, nrSeqContrato })
        .then(response => {
          const { body } = response;
          if (!body) return;

          this.updateFetchSCA(body?.nrSeqPlano);
          this.formDadosBeneficiario.setData(body);
          this.formDadosContratacao.setData(this.getDadosContratacaoFromBody(body));
          catchRenderException(() => this.formPortabilidade.setData(this.getPortabilidadeFromBody(body)));
          catchRenderException(() => this.formCompartilhamentoRisco.setData(this.getCompartilhamentoRiscoFromBody(body)));

          const motivoInclusao = (body as unknown as BeneficiarioInclusaoEntity).motivoInclusao;
          if (!motivoInclusao || motivoInclusao.iePortabilidade != 'S') {
            this.hiddenSteps(this.identifierPortabilidade, false);
          }

          setTimeout(() => this.definirAtributos(this.formDadosContratacao));
        });

    } else {
      this.hiddenSteps(this.identifierPortabilidade, false);
      setTimeout(() => this.definirAtributos(this.formDadosContratacao));
    }
  }

  @OnMount(forms)
  onMount(event: MountEvent): void {
    this.formsApi.push(event.target as FormAPI);
  }

  @OnMount([
    Aliases.FIELD_DT_CONTRATACAO,
    Aliases.FIELD_DT_INCLUSAO_OPERADORA,
    Aliases.FIELD_MOTIVO_INCLUSAO,
    Aliases.FIELD_NR_SEQ_PLANO,
    Aliases.FIELD_NR_SEQ_SUBESTIPULANTE,
    Aliases.FIELD_NR_SEQ_TITULAR_SOLIC,
    Aliases.FIELD_NR_SEQ_TITULAR_CONTRATO,
    Aliases.FIELD_NR_SEQ_PARENTESCO,
    Aliases.FIELD_DT_ADMISSAO,
  ])
  onMountFieldsFormDadosContratacao(event: MountEvent): void {
    this.customFieldsFormDadosContratacao.push(event.target as FieldAPI);
  }

  @OnMount([
    Aliases.FIELD_NR_CPF,
    Aliases.FIELD_NM_PESSOA_FISICA,
    Aliases.FIELD_NM_SOCIAL,
    Aliases.FIELD_DT_NASCIMENTO,
    Aliases.FIELD_IE_SEXO,
    Aliases.FIELD_IE_ESTADO_CIVIL,
    Aliases.FIELD_DT_EMISSAO_CERT_CASAMENTO,
    Aliases.FIELD_NR_CERT_CASAMENTO,
    Aliases.FIELD_DT_ADOCAO,
    Aliases.FIELD_NM_MAE,
    Aliases.FIELD_NM_PAI,
    Aliases.FIELD_CD_NACIONALIDADE,
    Aliases.FIELD_SG_ESTADO_NASC,
    Aliases.FIELD_CD_MUNICIPIO_IBGE_NASC,
    Aliases.FIELD_DS_MUNICIPIO_NASC_ESTRANGEIRO,
    Aliases.FIELD_NR_IDENTIDADE,
    Aliases.FIELD_NR_REG_GERAL_ESTRANG,
    Aliases.FIELD_DT_EMISSAO_CI,
    Aliases.FIELD_DS_ORGAO_EMISSOR_CI,
    Aliases.FIELD_SG_EMISSORA_CI,
    Aliases.FIELD_NR_SEQ_PAIS,
    Aliases.FIELD_NR_TITULO_ELEITOR,
    Aliases.FIELD_NR_CARTAO_NAC_SUS,
    Aliases.FIELD_CD_DECLARACAO_NASC_VIVO,
    Aliases.FIELD_FAIXA_SALARIAL,
    Aliases.FIELD_CBO,
    Aliases.FIELD_NR_PIS_PASEP,
    Aliases.FIELD_NR_CTPS,
    Aliases.FIELD_NR_SERIE_CTPS,
    Aliases.FIELD_UF_EMISSORA_CTPS,
    Aliases.FIELD_CHECK_ENDERECO_TITULAR,
    Aliases.FIELD_CD_CEP,
    Aliases.FIELD_CD_TIPO_LOGRADOURO,
    Aliases.FIELD_DS_ENDERECO,
    Aliases.FIELD_NR_ENDERECO,
    Aliases.FIELD_DS_COMPLEMENTO,
    Aliases.FIELD_DS_BAIRRO,
    Aliases.FIELD_SG_ESTADO,
    Aliases.FIELD_DS_MUNICIPIO,
    Aliases.FIELD_TELEFONE,
    Aliases.FIELD_CELULAR,
    Aliases.FIELD_DS_EMAIL,
    Aliases.FIELD_NR_SEQ_LOCALIZACAO_BENEF,
    Aliases.FIELD_CD_MATRICULA_EST,
    Aliases.FIELD_NR_SEQ_VINCULO_ESTIP,
    Aliases.FIELD_OPERADORA_ANT,
    Aliases.FIELD_CD_SISTEMA_ANTERIOR,
    Aliases.FIELD_CD_CARTEIRA_ANT,
    Aliases.FIELD_DS_OBSERVACAO,
    Aliases.FIELD_CONGENERE,
    Aliases.FIELD_ANEXOS,
  ])
  onMountFieldsFormDadosBeneficiario(event: MountEvent): void {
    this.customFieldsFormDadosBeneficiario.push(event.target as FieldAPI);
  }

  @OnMount(Aliases.FORM_DADOS_CONTRATACAO)
  onMountFormDadosContratacao(event: MountEvent): void {
    const api = event.target as FormAPI;
    this.formsApi.push(api);

    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const currentContract = StipulatorContext.getCurrentContract();

    void HttpService.get(Endpoint.GET.INC_BENEF_DT_ADESSAO, { nrSeqContrato })
      .then(response => {
        const { dtAdesao = '', ieAlteraData } = response.body as DataAdesao;
        const { nrSeqSubestipulante } = currentContract;

        !this.isDtContratacaoReadonly && (this.isDtContratacaoReadonly = 'N' === ieAlteraData);
        !this.isNrSeqSubestipulanteReadonly && (this.isNrSeqSubestipulanteReadonly = !!nrSeqSubestipulante);

        this.dateFieldDtContratacao.setReadOnly(this.isDtContratacaoReadonly);
        this.autocompleteNrSeqSubestipulante.setReadOnly(this.isNrSeqSubestipulanteReadonly);

        api.setData({ ...api.getRecord(), dtContratacao: dtAdesao, ...(nrSeqSubestipulante && { nrSeqSubestipulante }) });

        setTimeout(() => api.validate());
      });
  }

  @OnMount(Aliases.FILE_UPLOADER_ANEXOS)
  onMountFileUploader(event: MountEvent): void {
    const api = event.target as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;

    setPredefinedFiles(15, api)
      .then(anexos => {
        this.hiddenSteps(this.identifierAnexos, !isEmptyArrayOrObject(anexos));
        this.regraAnexos = anexos as unknown as Anexo[];
      })
      .catch(() => this.hiddenSteps(this.identifierAnexos, false));
  }

  @OnMount(Aliases.MAPPER_ANEXOS)
  onMountMapperAnexos(event: MountEvent): void {
    const api = event.target as MapperAPI;

    const { nrSeqContrato, nrSeqInclusao } = this.historyService.getCurrentRouteParams();

    if (nrSeqInclusao) {
      void HttpService.get(Endpoint.GET.INC_BENEF_ANEXOS, { nrSeqInclusao, nrSeqContrato })
        .then(resp => {
          const { body } = resp;
          if (body) {
            api.setData(body as Record<string, Value>);

            catchRenderException(() => {
              if (this.isReenvio) {
                if ((body as []).length) {
                  this.fieldCheckboxAnexos.setVisible(true);
                  this.fieldAnexos.setVisible(false);
                  this.formAnexos.setData({ ...this.formAnexos.getRecord(), copiarAnexos: true });
                }
              }
            });
          }
        });
    }
  }

  @OnBeforeFetch([Aliases.FETCH_TITULARES_PROCESSO_INCLUSAO, Aliases.FETCH_TITULARES_CONTRATO])
  onBeforeFetch(event: AbortableFetchEvent): void {
    const api = event.target as FetchAPI;
    const nrSeqSubestipulante = this.context.get('nrSeqSubestipulante');

    nrSeqSubestipulante && api.setParameters({ ...api.getParameters(), nrSeqSubestipulante });
  }

  @OnChange(Aliases.FIELD_NR_SEQ_PLANO)
  onChangePlan(event: ChangeEvent<Value>): void {
    const nrSeqPlano = event.value;
    this.context.set({ nrSeqPlano });
    this.hiddenSteps(this.identifierSca, true);
    this.updateFetchSCA(nrSeqPlano);
  }

  @OnBeforeFetch(Aliases.FETCH_SCA)
  onBeforeFetchSca(event: AbortableFetchEvent): void {
    const { nrSeqInclusao } = this.historyService.getCurrentRouteParams();
    if (nrSeqInclusao) {
      const api = event.target as FetchAPI;
      api.setParameters({ ...api.getParameters(), nrSeqInclusao });
    }
  }

  @OnAfterFetch(Aliases.FETCH_SCA)
  onAfterFetchSca(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const { nrSeqInclusao } = this.historyService.getCurrentRouteParams();
    const data = event.data as [];

    if (nrSeqInclusao && (data && !!data.length)) {
      const dataSca = this.checklistSca.getData() as unknown as Record<string, string>[];
      const values = dataSca.filter(item => item.ieMarcado === 'S');

      this.formSca.setData({ scas: values });
    }
  }

  @OnValidate(Aliases.FORM_DADOS_CONTRATACAO)
  onValidateFormDadosContratacao(event: ValidateEvent): void {
    const api = event.target as FormAPI;
    this.setStepDone(this.getIdentifierFromAlias(api.state.alias as string), api.isValid());
  }

  @OnValidate(forms)
  onValidate(event: ValidateEvent): void {
    const api = event.target as FormAPI;
    this.setStepDone(this.getIdentifierFromAlias(api.state.alias as string), api.isValid());
  }

  @OnAfterNavigate(Aliases.WIZARD_NOVA_INCLUSAO)
  onAfterNavigate(event: AbortableNavigateEvent<string>): void {
    const { from, to } = event;

    let form = this.getFormByIdentifier(from);
    form.submit();
    form.setEditing(false);

    if (!to) return;

    form = this.getFormByIdentifier(to);
    if (form) {

      form.setEditing(true);
      this.wizardNovaInclusao.setStepDone(to, form.isValid());

      if (to == this.identifierDadosContratacao) {
        setTimeout(() => {
          this.dateFieldDtContratacao.setReadOnly(this.isDtContratacaoReadonly);
          this.autocompleteNrSeqSubestipulante.setReadOnly(this.isNrSeqSubestipulanteReadonly);
        });
      }
    }
  }

  @OnChange(Aliases.AUTOCOMPLETE_MOTIVO_INCLUSAO)
  onChangeMotivoInclusao(e: ChangeEvent<string>): void {
    const motivoInclusao = e.value as unknown as Record<string, Value>;
    this.hiddenSteps(this.identifierPortabilidade, motivoInclusao && motivoInclusao.iePortabilidade === 'S');
  }

  @OnChange(Aliases.AUTOCOMPLETE_SG_ESTADO)
  onChangeSgEstado(e: ChangeEvent<string>): void {
    this.fieldDsMunicipio.setValue(null);
    this.fetchDsMunicipio.setParameters({ dsUnidadeFederacao: e.value });
    this.fetchDsMunicipio.forceFetch();
  }

  @OnChange(Aliases.FIELD_CHECK_ENDERECO_TITULAR)
  onChangeCheckEnderecoTitular(event: ChangeEvent<boolean>): void {
    if (event.value) {
      const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
      const { nrSeqTitularContrato, nrSeqTitularSolic } = this.formDadosContratacao.getRecord();

      let endpoint;
      let nrSeqTitular;

      if (nrSeqTitularContrato) {
        endpoint = Endpoint.GET.ENDERECO_TITULAR_CONTRATO;
        nrSeqTitular = nrSeqTitularContrato;

      } else if (nrSeqTitularSolic) {
        endpoint = Endpoint.GET.ENDERECO_TITULAR_SOLIC;
        nrSeqTitular = nrSeqTitularSolic;
      }

      endpoint && void HttpService.get<EnderecoData>(endpoint, { nrSeqContrato, nrSeqTitular })
        .then((response) => this.setEndereco(response));
    }
  }

  @OnChange(Aliases.FIELD_CHECKBOX_ANEXOS)
  onChangeCheckboxAnexos(event: ChangeEvent<boolean>): void {
    this.fieldAnexos.setVisible(!event.value);
    this.panelListaAnexos.setVisible(event.value);
    this.toastNotification.setVisible(false);
  }

  @OnChange(Aliases.FORM_DADOS_CONTRATACAO)
  onChangeForm(event: ChangeEvent<FormAPI>): void {
    this.definirAtributos(event.target as unknown as FormAPI);
  }

  @OnBlur(Aliases.FIELD_CD_CEP)
  onBlurCdCep(event: FocusEvent): void {
    const api = event.target as unknown as FieldAPI;

    if (api.isValid() && api.getValue()) {
      void HttpService.get<EnderecoData>(Endpoint.GET.ENDERECO_CEP, { cdCep: api.getValue() })
        .then((response) => this.setEndereco(response));
    }
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST)
  onSelectAttachment(event: SelectEvent<Anexo>): void {
    const { value } = event;
    const { nrSeqAnexo, filename } = value;

    const { nrSeqContrato, nrSeqInclusao } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.INCLUSAO_ANEXOS;

    FileService.download(Endpoint.GET.INC_BENEF_DOWNLOAD_ANEXO, filename, { ieTabela, nrSeqAnexo, nrSeqContrato, nrSeqInclusao }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @OnChange(Aliases.FIELD_NR_SEQ_PLANO)
  onChangeProduct(e: ChangeEvent<number>): void {
    const nrSeqPlanoBenef = e.value;
    this.updateFetchSCA(nrSeqPlanoBenef);
  }

  private setEndereco(response: RequestResponse<EnderecoData>): void {
    if (response.body) {
      const { dsUf } = response.body;

      this.formDadosBeneficiario.setData({
        ...this.formDadosBeneficiario.getRecord(),
        ...response.body,
        sgEstado: dsUf,
      });

      if (dsUf) {
        this.context.set({ dsUnidadeFederacaoSgEstado: dsUf });
        this.fetchDsMunicipio.forceFetch();
      }
    }
  }

  private setStepDone(identifier: string, valid: boolean): void {
    if (this.wizardNovaInclusao.getCurrentStepIdentifier() == identifier) {
      this.wizardNovaInclusao.setStepDone(identifier, valid);
    }
  }

  private setCustomFields(data: Atributo[], fields: FieldAPI[]): void {

    const customEntries = { nrSeqMotivoInclusao: 'motivoInclusao', cdCbo: 'cbo', nrSeqOperadoraAnt: 'operadoraAnt', nrSeqFaixaSalarial: 'faixaSalarial' } as Record<string, string>;

    data.forEach(atributo => {
      const field = fields.find(field => field.getEntry() == atributo.nmAtributo || customEntries[atributo.nmAtributo] == field.getEntry());
      if (field) {
        if (atributo.ieObrigatorio === 'S') {
          field.setValidators([{ name: 'required', options: {} }] as Validator[]);
          field.validate();
          return;
        }
        if (atributo.ieVisualizarCampo === 'N') {
          field.setVisible(false);
        }
      }
    });
  }

  private setHiddenCustomWizardStep(data: Atributo[], fieldEntry: string, identifier: string): void {
    const atributo = data.find(atributo => atributo.nmAtributo == fieldEntry);
    if (atributo) {
      this.hiddenSteps(identifier, atributo.ieVisualizarCampo !== 'N');
    }
  }

  private getFormByIdentifier(identifier: string): FormAPI {
    const form = this.formsApi.find(form => this.getIdentifierFromAlias(form.state.alias as string) == identifier);

    if (!form) throw `Identifier [${identifier}]: FormAPI unknown`;

    return form;
  }

  private getIdentifierFromAlias(alias: string): string {
    return `wizard-step${alias.replace('form', '')}`;
  }

  private hiddenSteps(identifier: string, visible: boolean = true): void {
    if (visible) {
      this.wizardNovaInclusao.setHiddenSteps(this.wizardNovaInclusao.getHiddenSteps().filter(hidden => hidden != identifier));
    } else {
      this.wizardNovaInclusao.setHiddenSteps([...this.wizardNovaInclusao.getHiddenSteps(), identifier]);
    }
  }

  private getFetch(body: {}): Promise<RequestResponse> {
    const { nrSeqContrato, nrSeqInclusao } = this.historyService.getCurrentRouteParams();
    const parameters = { ...(nrSeqInclusao && { nrSeqInclusao }), nrSeqContrato };

    let endpoint = Endpoint.POST.INC_BENEF_INCLUIR;
    let method = HttpMethod.POST;

    if (nrSeqInclusao) {
      endpoint = Endpoint.PUT.INC_BENEF_EDITAR;
      method = HttpMethod.PUT;

      if (this.isReenvio) {
        endpoint = Endpoint.POST.INC_BENEF_REENVIAR;
        method = HttpMethod.POST;
      }
    }

    return HttpService.fetch(endpoint, method, parameters, { body });
  }

  private getDadosContratacaoFromBody(body: Record<string, Value>): {} {
    return this.assign(body,
      [
        'dtContratacao', 'dtInclusaoOperadora', 'motivoInclusao', 'nrSeqPlano',
        'nrSeqSubestipulante', 'nrSeqTitularSolic', 'nrSeqTitularContrato', 'nrSeqParentesco',
      ]);
  }

  private getPortabilidadeFromBody(body: Record<string, Value>): {} {
    return this.assign(body,
      [
        'nrRegistroOpsAns', 'ieAbrangenciaPlanoOrigem', 'ieTpContratacaoPlanoOrigem', 'nrRegistroPlanoOrigem',
        'vlPrecoPlanoOrigem', 'dtInclusaoOpsOrigem', 'nrProtocoloPortabilidade', 'dtEmissaoProtPortab',
      ]);
  }

  private getCompartilhamentoRiscoFromBody(body: Record<string, Value>): {} {
    return this.assign(body, ['congenere']);
  }

  private assign(body: Record<string, Value>, entries: string[]): {} {
    let data = {};
    entries.forEach(entry => data = { ...data, ...(body[entry] && { [entry]: body[entry] }) });
    return data;
  }

  private validateStatus(): void {
    const { nrSeqContrato, nrSeqInclusao } = this.historyService.getCurrentRouteParams();

    void HttpService.get<BeneficiarioLoteData[]>(Endpoint.GET.BENEFICIARIO_INCLUSAO_STATUS, { nrSeqContrato, nrSeqInclusao })
      .then(response => {
        const { body } = response;

        if (!body) {
          return;
        }

        const { ieStatus, dsMotivoReprovacao, dsStatus } = body as unknown as BeneficiarioLoteData;

        if (ieStatus === StatusBeneficiario.RO) {
          this.panelControlCardApi.setVisible(true);
          this.textMotivoApi.setValue(dsMotivoReprovacao);
          this.stampSituacaoApi.setLabel(dsStatus);
        } else if (![StatusBeneficiario.I, StatusBeneficiario.AL].includes(ieStatus)) {
          this.wizardNovaInclusao.setReadOnly(true);
          this.formsApi.forEach(form => catchRenderException(() => form.setEditing(false)));
          this.isDtContratacaoReadonly = true;
          this.isNrSeqSubestipulanteReadonly = true;
        }
      })
      .catch(() => this.historyService.push(FULL_ROUTES.BENEF_NOVA_INCLUSAO_CONSULTA));
  }

  private definirAtributos(form: FormAPI): void {
    const { nrSeqContrato } = this.context.getBulkData();
    const { nrSeqTitularSolic, nrSeqTitularContrato } = form.getRecord();
    const ieTitularidade = nrSeqTitularSolic || nrSeqTitularContrato ? 'D' : 'T';

    this.fieldCheckEnderecoTitular.setVisible(ieTitularidade === 'D');

    void HttpService.get(Endpoint.GET.INC_BENEF_ATRIBUTOS, { nrSeqContrato, ieTitularidade })
      .then(response => {
        const data = response.body as Atributo[];
        this.setCustomFields(data, this.customFieldsFormDadosBeneficiario);

        this.setHiddenCustomWizardStep(data, 'nrSeqCongenere', this.identifierCompartilhamentoRisco);

        setTimeout(() => form.validate());
      });
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos?.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? '0';
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

  private updateFetchSCA(nrSeqPlanoBenef: Value): void {
    if (nrSeqPlanoBenef) {
      this.fetchSCA.setParameters({ ...this.fetchSCA.getParameters(), nrSeqPlanoBenef });
    }
    this.fetchSCA.forceFetch();
  }

}
